import React from 'react';
import { Controller, FieldPath, FieldValues, PathValue, UseControllerProps } from 'react-hook-form';
import { AppTextField, AppTextFieldProps } from '../fields/text/textField';

type HookFormTextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppTextFieldProps, 'value' | 'onChange'> & {
  ref?: React.ForwardedRef<HTMLInputElement>;
  name: TName;
  control: UseControllerProps<TFieldValues, TName>['control'];
  rules?: UseControllerProps<TFieldValues, TName>['rules'];
  onFocus?: () => void;
  decimal?: number;
  defaultValue?: PathValue<TFieldValues, TName>;
};

export function HookFormTextField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: HookFormTextFieldProps<TFieldValues, TName>) {
  if (props.disabled) {
    return (
      <Controller
        control={props.control}
        name={props.name}
        render={({ field: { value } }) => <AppTextField {...props} value={value ?? ''} />}
      />
    );
  }

  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { value, onChange, ref }, fieldState: { error }, formState: { isSubmitting } }) => (
        <AppTextField
          {...props}
          disabled={props.disabled || isSubmitting}
          ref={ref}
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          onBlur={(e) => {
            if (props.type === 'number' && props.decimal !== undefined && !!value) {
              onChange(Number(e.target?.value).toFixed(props.decimal));
            }

            props.onBlur?.(e);
          }}
          errorProps={{
            ...props.errorProps,
            errors: error?.message ?? props.errorProps?.errors,
          }}
        />
      )}
    />
  );
}
